/* Estimate AI chat — the conversational panel that lives beside the
   estimate document (EstimateDocEditor). It has TWO modes (DESIGN22 §C):

     • Edit document (default — today's behaviour): the user asks for a
       change in plain English; the panel auto-saves any pending manual
       edits, sends the instruction to POST /:id/prompt, adopts the
       server-authoritative spec back into the editor and shows a
       deterministic, client-computed DIFF of what moved (case/module
       renames, day changes, +/− modules, notes, summary, and always the
       document Total when it shifts).
     • Ask: POST /:id/ask — a question ABOUT the estimate («why is this
       module seven days?»). The answer comes back as prose in a normal
       bubble; NO diff chips, NO document change, the editor is untouched.

   The panel owns only presentation; the edit correctness core (save race
   guard, adoption, changed-row flash + scroll) lives in the editor and is
   handed here through the single async `onSend` prop — unchanged.

   HISTORY is server-persisted (DESIGN22 §C3): the thread is loaded from
   GET /:id/chat on mount and cleared through DELETE /:id/chat, so it now
   survives a reload or a new tab. sessionStorage is gone as a source of
   truth. A replayed EDIT turn renders as the server's neutral prose line
   (diff chips are live-only, computed from the returned spec).

   Desktop: a 360px right side panel that shrinks the page canvas. Mobile
   (≤900px): a slide-up bottom sheet with a tap-to-close backdrop. English
   strings, scoped .arch-chat-* styles in architect.css. */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  EstimateSpec,
  EstimateDays,
  EstimateTelemetry,
  EstimateChatMessage,
  estimateApi,
} from './api';
import { ESTIMATE_MODELS } from './EstimatePanel';
import { autoGrow, fmtK } from './utils';
import { ArrowUp, MessageCircle, Pencil, Trash2, X } from 'lucide-react';
import { LatoOrb, useLatoAiPanelDrag } from './LatoAi';
import { StageAiButton } from './StageChrome';

/* ── shared number / day formatting (kept local so the chat has no
      import cycle with the editor's private helpers) ──────────── */

function numStr(n: number): string {
  return Number.isFinite(n) ? String(n) : '';
}

/** A stored days value → a compact label ("2", "1–2", "TBD", "–"). */
export function fmtDays(d: EstimateDays): string {
  if (d === 'TBD') return 'TBD';
  if (d === '–' || (d as unknown) === '-') return '–';
  if (Array.isArray(d)) return numStr(d[0]) + '–' + numStr(d[1]);
  if (typeof d === 'number') return numStr(d);
  return '';
}

const isNumeric = (d: EstimateDays): boolean =>
  typeof d === 'number' || Array.isArray(d);

function daysEqual(a: EstimateDays, b: EstimateDays): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a[0] === b[0] && a[1] === b[1];
  return a === b;
}

interface MinMax {
  min: number;
  max: number;
}

/** Document day-total from a clean spec (mirrors the backend recompute:
    numeric days sum; ranges add their bounds; TBD/– excluded). */
function specTotal(spec: EstimateSpec): MinMax {
  let min = 0;
  let max = 0;
  for (const c of spec.cases || []) {
    for (const m of c.modules || []) {
      const d = m.days;
      if (Array.isArray(d)) {
        min += d[0];
        max += d[1];
      } else if (typeof d === 'number' && Number.isFinite(d)) {
        min += d;
        max += d;
      }
    }
  }
  return { min, max };
}

function daysLabel(t: MinMax): string {
  return t.min === t.max
    ? numStr(t.min) + ' days'
    : numStr(t.min) + '–' + numStr(t.max) + ' days';
}

function fmtWeeks(days: number): string {
  const w = Math.round((days / 5) * 2) / 2;
  return Number.isInteger(w) ? String(w) : w.toFixed(1);
}

function weeksLabel(t: MinMax): string {
  if (t.min === 0 && t.max === 0) return '0 weeks';
  const lo = fmtWeeks(t.min);
  const hi = fmtWeeks(t.max);
  if (lo === hi) return lo + ' week' + (lo === '1' ? '' : 's');
  return lo + '–' + hi + ' weeks';
}

const norm = (s: string | undefined | null): string => (s || '').trim();
const sameName = (a: string | undefined, b: string | undefined): boolean =>
  norm(a).toLowerCase() === norm(b).toLowerCase();

/* ── deterministic diff ─────────────────────────────────────── */

export type ChipKind =
  | 'add'
  | 'remove'
  | 'day'
  | 'rename'
  | 'note'
  | 'summary'
  | 'total'
  | 'more';

export interface DiffChip {
  id: string;
  kind: ChipKind;
  label: string;
}

/** A stable key for a module row (case name + module name), used both to
    tag diff results and to flash the matching row after adoption. Trimmed
    so server-trimmed and editor-trimmed names line up. */
export function moduleKey(caseName: string, moduleName: string): string {
  return norm(caseName) + '' + norm(moduleName);
}

interface Matcher<T> {
  used: Set<number>;
  pool: T[];
}
function makeMatcher<T>(pool: T[]): Matcher<T> {
  return { used: new Set(), pool };
}
/** Match by name first (case-insensitive, unused), else fall back to the
    same index when still free — mirrors the contract's matching rule. */
function matchByName<T extends { name?: string }>(
  m: Matcher<T>,
  name: string | undefined,
  idx: number,
): { item: T; index: number } | null {
  let j = m.pool.findIndex((p, i) => !m.used.has(i) && sameName(p.name, name));
  if (j < 0 && idx < m.pool.length && !m.used.has(idx)) j = idx;
  if (j < 0) return null;
  m.used.add(j);
  return { item: m.pool[j], index: j };
}

/** The set of module keys that were ADDED or CHANGED between two specs —
    the rows the editor flashes after adopting the new spec. Removed rows
    are gone, so they never flash. */
export function changedModuleKeys(
  before: EstimateSpec,
  after: EstimateSpec,
): Set<string> {
  const keys = new Set<string>();
  const caseMatcher = makeMatcher(before.cases || []);
  for (let ci = 0; ci < (after.cases || []).length; ci++) {
    const ac = (after.cases || [])[ci];
    const bcMatch = matchByName(caseMatcher, ac.name, ci);
    const modMatcher = makeMatcher(bcMatch ? bcMatch.item.modules || [] : []);
    for (let mi = 0; mi < (ac.modules || []).length; mi++) {
      const am = ac.modules[mi];
      const k = moduleKey(ac.name, am.name);
      const bmMatch = bcMatch
        ? matchByName(modMatcher, am.name, mi)
        : null;
      if (!bmMatch) {
        keys.add(k); // added
        continue;
      }
      const bm = bmMatch.item;
      if (
        !daysEqual(bm.days, am.days) ||
        norm(bm.description) !== norm(am.description) ||
        (bm.apis || []).join('|') !== (am.apis || []).join('|')
      ) {
        keys.add(k);
      }
    }
  }
  return keys;
}

/** First changed row key in document order (for scroll-into-view). */
export function firstChangedKey(
  after: EstimateSpec,
  keys: Set<string>,
): string | null {
  for (const c of after.cases || []) {
    for (const m of c.modules || []) {
      const k = moduleKey(c.name, m.name);
      if (keys.has(k)) return k;
    }
  }
  return null;
}

let chipSeq = 0;
const chip = (kind: ChipKind, label: string): DiffChip => ({
  id: 'chip' + ++chipSeq,
  kind,
  label,
});

function dayChip(ci: number, moduleName: string, before: EstimateDays, after: EstimateDays): DiffChip {
  const suffix = isNumeric(before) && isNumeric(after) ? ' days' : '';
  const label =
    'Case ' + (ci + 1) + ' · ' + norm(moduleName) + ': ' +
    fmtDays(before) + ' → ' + fmtDays(after) + suffix;
  return chip('day', label);
}

/** The full ordered chip list for the assistant bubble. Capped at 8
    visible chips (+N more), but the Total chip is always kept last. */
export function computeDiff(before: EstimateSpec, after: EstimateSpec): DiffChip[] {
  const other: DiffChip[] = [];
  const caseMatcher = makeMatcher(before.cases || []);
  const aCases = after.cases || [];

  for (let ci = 0; ci < aCases.length; ci++) {
    const ac = aCases[ci];
    const bcMatch = matchByName(caseMatcher, ac.name, ci);
    if (!bcMatch) {
      other.push(chip('add', '+ Case "' + norm(ac.name) + '"'));
      for (const am of ac.modules || []) {
        other.push(chip('add', '+ Module "' + norm(am.name) + '" (Case ' + (ci + 1) + ')'));
      }
      continue;
    }
    const bc = bcMatch.item;
    if (!sameName(bc.name, ac.name)) {
      other.push(chip('rename', 'Case renamed: ' + norm(bc.name) + ' → ' + norm(ac.name)));
    }
    const modMatcher = makeMatcher(bc.modules || []);
    for (let mi = 0; mi < (ac.modules || []).length; mi++) {
      const am = ac.modules[mi];
      const bmMatch = matchByName(modMatcher, am.name, mi);
      if (!bmMatch) {
        other.push(chip('add', '+ Module "' + norm(am.name) + '" (Case ' + (ci + 1) + ')'));
        continue;
      }
      const bm = bmMatch.item;
      if (!sameName(bm.name, am.name)) {
        other.push(chip('rename', 'Module renamed: ' + norm(bm.name) + ' → ' + norm(am.name)));
      }
      if (!daysEqual(bm.days, am.days)) {
        other.push(dayChip(ci, am.name, bm.days, am.days));
      }
    }
    // removed modules within this case
    (bc.modules || []).forEach((bm, i) => {
      if (!modMatcher.used.has(i)) other.push(chip('remove', '− Module "' + norm(bm.name) + '"'));
    });
  }
  // removed cases
  (before.cases || []).forEach((bc, i) => {
    if (!caseMatcher.used.has(i)) other.push(chip('remove', '− Case "' + norm(bc.name) + '"'));
  });

  // notes
  const bn = (before.notes || []).map(norm).filter(Boolean);
  const an = (after.notes || []).map(norm).filter(Boolean);
  if (an.length > bn.length) other.push(chip('note', 'Note added'));
  else if (an.length < bn.length) other.push(chip('note', 'Note removed'));
  else if (an.join('') !== bn.join('')) other.push(chip('note', 'Notes updated'));

  // summary
  if (
    norm(before.summary?.deliverables) !== norm(after.summary?.deliverables) ||
    norm(before.summary?.team) !== norm(after.summary?.team)
  ) {
    other.push(chip('summary', 'Summary updated'));
  }

  // total (always kept, always last)
  const bt = specTotal(before);
  const at = specTotal(after);
  const totalChip =
    bt.min !== at.min || bt.max !== at.max
      ? chip('total', 'Total: ' + daysLabel(bt) + ' → ' + daysLabel(at))
      : null;

  const maxOther = totalChip ? 7 : 8;
  let shown = other;
  if (other.length > maxOther) {
    const overflow = other.length - (maxOther - 1);
    shown = other.slice(0, maxOther - 1).concat(chip('more', '+' + overflow + ' more'));
  }
  return totalChip ? shown.concat(totalChip) : shown;
}

/* ── telemetry footnote ─────────────────────────────────────── */

function telemetryText(t: EstimateTelemetry): string {
  const inT = t.inputTokens ?? 0;
  const outT = t.outputTokens ?? 0;
  const cache = (t.cacheReadTokens ?? 0) + (t.cacheCreationTokens ?? 0);
  const parts = ['↑' + fmtK(inT) + ' ↓' + fmtK(outT) + ' tokens'];
  if (cache > 0) parts.push('cache ' + fmtK(cache));
  if (typeof t.costUsd === 'number' && t.costUsd > 0) {
    parts.push('$' + t.costUsd.toFixed(t.costUsd < 0.1 ? 3 : 2));
  }
  return parts.join(' · ');
}

/* ── message model + server-persisted history ───────────────── */

/** Which half of the panel a turn belongs to: an instruction that edits the
    document, or a question that only reads it. */
export type ChatMode = 'edit' | 'ask';

type ChatMessage =
  | { id: string; role: 'user'; text: string; mode: ChatMode }
  | { id: string; role: 'system'; text: string }
  /* a live edit result — the client-computed diff of before → after */
  | {
      id: string;
      role: 'assistant';
      kind: 'diff';
      status: string;
      chips: DiffChip[];
      totalLine: string;
      telemetry: string | null;
    }
  /* prose: an Ask answer, or a replayed turn from the stored thread */
  | {
      id: string;
      role: 'assistant';
      kind: 'answer';
      text: string;
      mode: ChatMode;
      telemetry: string | null;
    }
  | {
      id: string;
      role: 'error';
      text: string;
      instruction: string;
      model: string;
      mode: ChatMode;
    };

/** In-memory ceiling. Matches the server's own cap on GET /:id/chat so a
    restored thread is never truncated by the client. */
const HISTORY_CAP = 200;

let msgSeq = 0;
const nextId = (): string => 'msg' + Date.now().toString(36) + (++msgSeq);

/** One stored row → a renderable message (unknown roles are dropped, so a
    future server-side role can't break an old client). */
function fromServer(m: EstimateChatMessage): ChatMessage | null {
  if (!m || typeof m !== 'object') return null;
  const text = String(m.content || '').trim();
  if (!text) return null;
  const mode: ChatMode = m.mode === 'ask' ? 'ask' : 'edit';
  const id = 'srv' + String(m.id || nextId());
  if (m.role === 'user') return { id, role: 'user', text, mode };
  if (m.role !== 'assistant') return null;
  const t = m.telemetry;
  return {
    id,
    role: 'assistant',
    kind: 'answer',
    text,
    mode,
    telemetry: t && typeof t === 'object' ? telemetryText(t) : null,
  };
}

const EDIT_PROMPTS = [
  'Add a DocuSign e-signature module to Case 1',
  'Make the timelines more conservative',
  'Split the biggest case into two',
];

const ASK_PROMPTS = [
  'Why does Case 1 carry these days?',
  'What scope could be missing from this estimate?',
  'Which third-party integrations are covered here?',
];

/* ── props ──────────────────────────────────────────────────── */

export interface ChatEditResult {
  ok: boolean;
  before?: EstimateSpec;
  after?: EstimateSpec;
  telemetry?: EstimateTelemetry | null;
  error?: string;
}

interface Props {
  estimateId: string;
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
  /** The embedded editor places its launcher in the document toolbar. */
  launcherTarget?: HTMLElement | null;
  openDisabled?: boolean;
  /** Approved estimates can answer questions, but cannot run AI edits. */
  askOnly?: boolean;
  /** True while the editor's manual Save is in flight — send is disabled. */
  saving: boolean;
  /** The correctness core (auto-save → snapshot → POST → adopt → flash),
      owned by the editor. Resolves with before/after specs + telemetry on
      success, or { ok:false, error } otherwise. Calls hooks.onSavedManual
      the moment pending manual edits were persisted. */
  onSend: (
    instruction: string,
    model: string,
    hooks: { onSavedManual: () => void },
  ) => Promise<ChatEditResult>;
}

/* ── component ──────────────────────────────────────────────── */

export default function EstimateChat({ estimateId, open, onClose, onOpen, launcherTarget, openDisabled, askOnly = false, saving, onSend }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [model, setModel] = useState(ESTIMATE_MODELS[0].value);
  /* Edit is the default: it is what this panel has always done. */
  const [mode, setMode] = useState<ChatMode>('edit');
  /* The mode of the turn actually in flight (the toggle is locked while a
     request runs, but a retry can re-arm the other mode). */
  const [pendingMode, setPendingMode] = useState<ChatMode>('edit');
  const [inFlight, setInFlight] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);

  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const stickRef = useRef(true); // auto-scroll unless the user scrolled up
  const inFlightRef = useRef(false);

  /* Load the stored thread for this estimate (and reload it when switching
     documents). Anything typed while the request is in flight is kept: the
     restored rows are older by construction, so they go in FRONT of it.
     A failure is silent — history is a convenience, never a blocker. */
  useEffect(() => {
    let alive = true;
    setMessages([]);
    setHistoryLoading(true);
    stickRef.current = true;
    estimateApi
      .getEstimateChat(estimateId)
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r?.messages) ? r.messages : [];
        const restored = rows
          .map(fromServer)
          .filter((m): m is ChatMessage => m !== null);
        if (!restored.length) return;
        setMessages((cur) => [...restored, ...cur].slice(-HISTORY_CAP));
      })
      .catch(() => {
        /* no stored thread (or an older backend) — start empty */
      })
      .finally(() => {
        if (alive) setHistoryLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [estimateId]);

  /* elapsed ticker while a request is in flight */
  useEffect(() => {
    if (!inFlight) return undefined;
    const started = Date.now();
    setElapsedMs(0);
    const t = window.setInterval(() => setElapsedMs(Date.now() - started), 250);
    return () => window.clearInterval(t);
  }, [inFlight]);

  /* stick to bottom on new content unless the user scrolled up */
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = messages.length ? el.scrollHeight : 0;
  }, [messages, inFlight, open]);

  const onListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }, []);

  /* focus the composer when the panel opens */
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => taRef.current?.focus({ preventScroll: true }), 220);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const push = useCallback((m: ChatMessage) => {
    setMessages((xs) => [...xs, m].slice(-HISTORY_CAP));
  }, []);

  const growTa = useCallback(() => {
    const ta = taRef.current;
    if (ta) autoGrow(ta);
  }, []);
  useLayoutEffect(growTa, [input, growTa]);

  /* ASK — question only. Never touches the document: the answer is prose
     and no spec comes back, so the editor is left exactly as it was. */
  const runAsk = useCallback(
    async (question: string, useModel: string) => {
      try {
        const r = await estimateApi.askEstimate(estimateId, question, useModel);
        const answer = String(r?.answer || '').trim();
        push({
          id: nextId(),
          role: 'assistant',
          kind: 'answer',
          mode: 'ask',
          text: answer || 'No answer came back — try rephrasing the question.',
          telemetry: r?.telemetry ? telemetryText(r.telemetry) : null,
        });
      } catch (e: any) {
        push({
          id: nextId(),
          role: 'error',
          text: e?.message || 'The question could not be answered — try again',
          instruction: question,
          model: useModel,
          mode: 'ask',
        });
      }
    },
    [estimateId, push],
  );

  /* EDIT — unchanged: the editor owns save → POST → adopt → flash. */
  const runEdit = useCallback(
    async (instruction: string, useModel: string) => {
      let savedNoted = false;
      const res = await onSend(instruction, useModel, {
        onSavedManual: () => {
          if (savedNoted) return;
          savedNoted = true;
          push({ id: nextId(), role: 'system', text: 'Saved your manual edits' });
        },
      });
      if (res.ok && res.after) {
        const chips = res.before ? computeDiff(res.before, res.after) : [];
        const t = specTotal(res.after);
        push({
          id: nextId(),
          role: 'assistant',
          kind: 'diff',
          status: 'Document updated — status back to review',
          chips,
          totalLine: daysLabel(t) + ' ~ ' + weeksLabel(t),
          telemetry: res.telemetry ? telemetryText(res.telemetry) : null,
        });
      } else {
        push({
          id: nextId(),
          role: 'error',
          text: res.error || 'The edit could not be completed — try again',
          instruction,
          model: useModel,
          mode: 'edit',
        });
      }
    },
    [onSend, push],
  );

  const runSend = useCallback(
    async (
      instruction: string,
      opts?: { model?: string; mode?: ChatMode },
    ) => {
      const text = instruction.trim();
      if (!text || inFlightRef.current || saving) return;
      const useModel = opts?.model || model;
      const useMode: ChatMode = askOnly ? 'ask' : opts?.mode || mode;
      stickRef.current = true;
      push({ id: nextId(), role: 'user', text, mode: useMode });
      setInput('');
      inFlightRef.current = true;
      setPendingMode(useMode);
      setInFlight(true);
      try {
        if (useMode === 'ask') await runAsk(text, useModel);
        else await runEdit(text, useModel);
      } finally {
        inFlightRef.current = false;
        setInFlight(false);
      }
    },
    [askOnly, mode, model, push, runAsk, runEdit, saving],
  );

  const retry = useCallback(
    (instruction: string, m: string, retryMode: ChatMode) => {
      setModel(m);
      setMode(retryMode);
      void runSend(instruction, { model: m, mode: retryMode });
    },
    [runSend],
  );

  /* «Clear history» now clears the SERVER thread — otherwise it would come
     straight back on the next reload. The local list is emptied only once
     the server confirms. */
  const clearHistory = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!messages.length) return;
    if (!window.confirm('Clear this chat history?')) return;
    try {
      await estimateApi.clearEstimateChat(estimateId);
      setMessages([]);
    } catch (e: any) {
      push({
        id: nextId(),
        role: 'system',
        text: e?.message || 'The history could not be cleared — try again',
      });
    }
  }, [estimateId, messages.length, push]);

  const composerSubmit = useCallback(() => {
    void runSend(input);
  }, [runSend, input]);

  const busy = inFlight;
  const panelDrag = useLatoAiPanelDrag(open);
  const asking = askOnly || mode === 'ask';
  const canSend = input.trim().length > 0 && !busy && !saving;

  return (
    <>
      {onOpen && (launcherTarget === undefined
        ? <StageAiButton open={open} busy={busy} disabled={!open && openDisabled} onClick={open ? onClose : onOpen} />
        : launcherTarget ? createPortal(
          <StageAiButton open={open} busy={busy} disabled={!open && openDisabled} onClick={open ? onClose : onOpen} />,
          launcherTarget,
        ) : null)}
      <div
        className={'arch-chat-backdrop' + (open ? ' open' : '')}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={panelDrag.panelRef}
        style={panelDrag.panelStyle}
        className={'arch-chat lato-ai-panel' + (open ? ' open' : '') + panelDrag.panelClass}
        inert={!open}
        aria-label="AI assistant"
        aria-hidden={open ? undefined : true}
      >
        <div className="arch-chat-panel">
        <button
          type="button"
          className="arch-chat-drag"
          aria-label="Close AI assistant"
          onClick={onClose}
        />
        <header className="arch-chat-head" {...panelDrag.handleProps} title="Drag to move Lato AI">
          <LatoOrb animated={open} busy={busy} />
          <div className="arch-chat-head-text">
            <div className="arch-chat-title">Lato AI</div>
            <div className="arch-chat-hint">
              {asking
                ? 'Ask about your saved estimate.'
                : 'Edit your estimate.'}
            </div>
          </div>
          <button
            className="arch-chat-headbtn"
            title="Clear chat history"
            aria-label="Clear chat history"
            onClick={() => void clearHistory()}
            disabled={busy || !messages.length}
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
          <button
            className="arch-chat-headbtn"
            title="Close AI assistant"
            aria-label="Close AI assistant"
            onClick={onClose}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="arch-chat-list" ref={listRef} onScroll={onListScroll}>
          {messages.length === 0 && historyLoading ? (
            <div className="arch-chat-loading">
              <span className="arch-chat-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              Loading the conversation…
            </div>
          ) : null}

          {messages.length === 0 && !busy && !historyLoading ? (
            <div className="arch-chat-empty">
              <div className="arch-chat-empty-mark"><LatoOrb /></div>
              <div className="arch-chat-empty-title">
                {asking ? 'A little clarity, a better plan.' : 'What would you like to refine?'}
              </div>
              <div className="arch-chat-empty-chips">
                {(asking ? ASK_PROMPTS : EDIT_PROMPTS).map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="arch-chat-quick"
                    onClick={() => {
                      setInput(q);
                      taRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {messages.map((m) => {
            if (m.role === 'user') {
              return (
                <div key={m.id} className="arch-chat-msg user">
                  <div className="arch-chat-bubble user">
                    <span className="arch-chat-tag">
                      {m.mode === 'ask' ? 'Question' : 'Edit'}
                    </span>
                    {m.text}
                  </div>
                </div>
              );
            }
            if (m.role === 'system') {
              return (
                <div key={m.id} className="arch-chat-system">
                  {m.text}
                </div>
              );
            }
            if (m.role === 'error') {
              return (
                <div key={m.id} className="arch-chat-msg assistant">
                  <div className="arch-chat-bubble error">
                    <div className="arch-chat-err-text">{m.text}</div>
                    {(m.mode === 'ask' || !askOnly) ? <button
                      type="button"
                      className="arch-chat-retry"
                      onClick={() => retry(m.instruction, m.model, m.mode)}
                      disabled={busy || saving}
                    >
                      ↻ Retry
                    </button> : null}
                  </div>
                </div>
              );
            }
            // assistant — prose (an Ask answer, or a replayed stored turn)
            if (m.kind === 'answer') {
              return (
                <div key={m.id} className="arch-chat-msg assistant">
                  <div className="arch-chat-bubble assistant">
                    {m.mode === 'ask' ? (
                      <span className="arch-chat-tag">Answer</span>
                    ) : null}
                    <div className="arch-chat-answer">{m.text}</div>
                    {m.telemetry ? <div className="arch-chat-tele">{m.telemetry}</div> : null}
                  </div>
                </div>
              );
            }
            // assistant — live edit result with its diff chips
            return (
              <div key={m.id} className="arch-chat-msg assistant">
                <div className="arch-chat-bubble assistant">
                  <div className="arch-chat-status">{m.status}</div>
                  {m.chips.length ? (
                    <div className="arch-chat-chips">
                      {m.chips.map((c) => (
                        <span key={c.id} className={'arch-chat-chip ' + c.kind}>
                          {c.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="arch-chat-chips-none">No structural changes detected.</div>
                  )}
                  <div className="arch-chat-total">{m.totalLine}</div>
                  {m.telemetry ? <div className="arch-chat-tele">{m.telemetry}</div> : null}
                </div>
              </div>
            );
          })}

          {busy ? (
            <div className="arch-chat-msg assistant">
              <div className="arch-chat-bubble assistant pending">
                <span className="arch-chat-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="arch-chat-pending-text">
                  {pendingMode === 'ask' ? 'Thinking…' : 'Editing the document…'}{' '}
                  {fmtElapsedLocal(elapsedMs)}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <form
          className="arch-chat-composer"
          onSubmit={(e) => {
            e.preventDefault();
            composerSubmit();
          }}
        >
          {/* mode toggle — which half of the panel is armed, always visible
              right above the input (segmented control, aria-pressed) */}
          <div
            className="arch-chat-modes"
            role="group"
            aria-label="Assistant mode"
          >
            <button
              type="button"
              className="arch-chat-mode"
              aria-pressed={asking}
              disabled={busy}
              title="Ask a question — the document is not changed"
              onClick={() => {
                setMode('ask');
                taRef.current?.focus({ preventScroll: true });
              }}
            >
              <span className="arch-chat-mode-ico" aria-hidden="true">
                <MessageCircle size={14} />
              </span>
              Ask
            </button>
            {!askOnly ? <button
              type="button"
              className="arch-chat-mode"
              aria-pressed={!asking}
              disabled={busy}
              title="Edit the document with an instruction"
              onClick={() => {
                setMode('edit');
                taRef.current?.focus({ preventScroll: true });
              }}
            >
              <span className="arch-chat-mode-ico" aria-hidden="true">
                <Pencil size={14} />
              </span>
              Edit
            </button> : null}
          </div>

          <textarea
            ref={taRef}
            className="arch-chat-input"
            rows={1}
            value={input}
            placeholder={
              asking
                ? 'Ask about this estimate — e.g. "why is Case 1 seven days?"…'
                : 'Describe a change — e.g. "add a DocuSign module to Case 1"…'
            }
            disabled={busy}
            aria-label={asking ? 'Ask about this estimate' : 'Describe a change'}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                composerSubmit();
              }
            }}
          />
          <div className="arch-chat-composer-row">
            <button
              type="submit"
              className="arch-btn arch-btn-primary arch-btn-sm arch-chat-send"
              title={asking ? "Ask Lato AI" : "Send edit"}
              disabled={!canSend}
            >
              <ArrowUp size={19} aria-hidden="true" /><span className="lato-ai-sr">{asking ? 'Ask' : 'Send'}</span>
            </button>
          </div>
          {busy ? (
            <div className="arch-chat-pending-inline" aria-hidden="true">
              <span className="arch-chat-dots">
                <i />
                <i />
                <i />
              </span>
              {pendingMode === 'ask' ? 'Thinking…' : 'Working…'}{' '}
              {fmtElapsedLocal(elapsedMs)}
            </div>
          ) : null}
        </form>
        </div>
      </aside>
    </>
  );
}

/** M:SS elapsed (kept local to avoid coupling to utils' formatter set). */
function fmtElapsedLocal(ms: number): string {
  const s = Math.floor(ms / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
